/**
 * LERNZENTRUM — Tour, die reine Hälfte (Paket B, Plan §1.3 und §2.1–2.3)
 *
 * Hier steht alles, was die Tour RECHNET: wo die Sprechblase hängt, wie das
 * Loch in der Maske aussieht, welcher Schritt als nächstes kommt, in welchem
 * Zustand die Tour ist und welcher Satz in der Blase steht. Kein DOM, kein
 * React, kein `window` — die Tests laufen in diesem Repo ohne jsdom, und das
 * ist der Grund, warum diese Datei existiert statt in `TourOverlay.tsx`
 * mitzulaufen (Plan §6: „Tour-Positionslogik (reine Funktion)").
 *
 * ─── Die eine Regel, die alles andere überstimmt ────────────────────────────
 *
 * Die Tour schreibt NIE Daten (Plan §1.3 und §9). `aktion: 'klick'` und
 * `'tippen'` sind Anweisungen an den Pipeline-Roboter im Demo-Projekt, nicht
 * an den Browser des Nutzers. Deshalb liefert `schrittText` bei `tippen` einen
 * Satz im Konjunktiv der Anleitung („Hier gibst du ein: …") und nirgends in
 * dieser Datei oder in `TourOverlay.tsx` steht ein `.click()` oder `.value =`.
 */

import type { TutorialAktion, TutorialKapitelId, TutorialRolle, TutorialSchritt } from '../../tutorial/types';

// ─── Formen ──────────────────────────────────────────────────────────────────

/** Ausschnitt aus `DOMRect` — bewusst nur die vier Zahlen, die wir brauchen. */
export interface Rechteck {
    top: number;
    left: number;
    breite: number;
    hoehe: number;
}

export interface Groesse {
    breite: number;
    hoehe: number;
}

/** Sichtfenster in CSS-Pixeln (`window.innerWidth/innerHeight`). */
export type Viewport = Groesse;

export type BlasenSeite = 'oben' | 'unten' | 'links' | 'rechts';

export interface BlasenPlatz {
    top: number;
    left: number;
    /** Auf welcher Seite des Ziels die Blase steht — für den Zeiger-Pfeil. */
    seite: BlasenSeite;
}

/**
 * Ab hier gilt die Blase als Bottom-Sheet. 640 px ist Tailwinds `sm`-Grenze;
 * dieselbe Zahl trennt im Bestand überall Handy von Desktop, und eine zweite
 * Grenze nur für die Tour wäre eine Quelle für „auf meinem Gerät sitzt es
 * anders".
 */
export const MOBIL_GRENZE = 640;

/** Standardabstand zwischen Loch und Blase. */
export const BLASEN_ABSTAND = 16;

// ─── Platzierung ─────────────────────────────────────────────────────────────

const klemme = (wert: number, min: number, max: number): number => {
    // max < min heisst: die Blase ist breiter/höher als das Sichtfenster.
    // Dann gewinnt der Anfang — lieber oben links angeschnitten als unten
    // rechts ausserhalb, wo niemand sie je zu sehen bekommt.
    if (max < min) return min;
    return Math.min(Math.max(wert, min), max);
};

/**
 * Wo die Sprechblase hängt.
 *
 * Reihenfolge der Versuche: **unten**, dann **oben**, dann **rechts**, dann
 * **links**. Unten zuerst, weil das Ziel in aller Regel ein Knopf ist, den man
 * gleich danach drücken soll — steht die Blase darüber, verdeckt der Mauszeiger
 * beim Hinfahren den Text. Passt keine Seite ganz, wird die zuletzt geprüfte
 * Lage ins Sichtfenster geklemmt; die Blase steht dann notfalls über dem Ziel,
 * aber nie ausserhalb des Bildschirms.
 *
 * Auf schmalen Geräten (≤ `MOBIL_GRENZE`) gibt es keine Wahl: die Blase ist ein
 * Bottom-Sheet über die volle Breite am unteren Rand. Neben einem Ziel ist auf
 * 360 px kein Platz, und eine Blase, die mitten im Bild über dem Ziel klebt,
 * verdeckt genau das, was sie erklärt.
 */
export const platziereBlase = (
    zielRect: Rechteck,
    blaseGroesse: Groesse,
    viewport: Viewport,
    abstand: number = BLASEN_ABSTAND,
): BlasenPlatz => {
    const { breite: bb, hoehe: bh } = blaseGroesse;

    if (viewport.breite <= MOBIL_GRENZE) {
        return { top: Math.max(0, viewport.hoehe - bh), left: 0, seite: 'unten' };
    }

    const mitteX = zielRect.left + zielRect.breite / 2;
    const mitteY = zielRect.top + zielRect.hoehe / 2;
    const waagerecht = klemme(mitteX - bb / 2, abstand, viewport.breite - bb - abstand);
    const senkrecht = klemme(mitteY - bh / 2, abstand, viewport.hoehe - bh - abstand);

    const unten = zielRect.top + zielRect.hoehe + abstand;
    if (unten + bh <= viewport.hoehe) return { top: unten, left: waagerecht, seite: 'unten' };

    const oben = zielRect.top - abstand - bh;
    if (oben >= 0) return { top: oben, left: waagerecht, seite: 'oben' };

    const rechts = zielRect.left + zielRect.breite + abstand;
    if (rechts + bb <= viewport.breite) return { top: senkrecht, left: rechts, seite: 'rechts' };

    const links = zielRect.left - abstand - bb;
    if (links >= 0) return { top: senkrecht, left: links, seite: 'links' };

    // Nichts passt (winziges Fenster, riesiges Ziel): unten geklemmt.
    return {
        top: klemme(unten, 0, Math.max(0, viewport.hoehe - bh)),
        left: klemme(waagerecht, 0, Math.max(0, viewport.breite - bb)),
        seite: 'unten',
    };
};

// ─── Maske ───────────────────────────────────────────────────────────────────

/** Füllregel, mit der `maskenPfad` gezeichnet werden MUSS — sonst kein Loch. */
export const MASKEN_FUELLREGEL = 'evenodd';

/** Auf eine Nachkommastelle — sonst stehen 14 Stellen Fliesskomma im DOM. */
const z = (n: number): string => String(Math.round(n * 10) / 10);

/** Das Loch etwas grösser als das Ziel: ein Knopf, der die Maske berührt, wirkt eingeklemmt. */
export const polstereRechteck = (rect: Rechteck, polster: number): Rechteck => ({
    top: rect.top - polster,
    left: rect.left - polster,
    breite: rect.breite + polster * 2,
    hoehe: rect.hoehe + polster * 2,
});

/**
 * Vollflächiges Rechteck mit einem abgerundeten Loch über dem Ziel.
 *
 * Zwei Teilpfade in EINEM `<path>`: aussen das Sichtfenster, innen das Loch.
 * Mit `fill-rule="evenodd"` (siehe `MASKEN_FUELLREGEL`) bleibt das Innere
 * ungefüllt. Ein zweiter, „ausgeschnittener" Weg über vier Rechtecke wäre
 * denkbar, hätte aber keine runden Ecken und vier Fugen, die sich bei
 * gebrochenen Gerätepixeln als helle Haarlinien zeigen.
 *
 * Das Loch wird NICHT ins Sichtfenster geklemmt: ein halb ausgescrolltes Ziel
 * soll auch halb freigestellt sein, und SVG beschneidet den Überstand selbst.
 */
export const maskenPfad = (zielRect: Rechteck, viewport: Viewport, radius: number): string => {
    const aussen = `M0 0H${z(viewport.breite)}V${z(viewport.hoehe)}H0Z`;
    const { top: y, left: x, breite: w, hoehe: h } = zielRect;

    // Kein messbares Ziel (Element noch nicht gelayoutet): dann eben nur Maske.
    if (w <= 0 || h <= 0) return aussen;

    const r = Math.max(0, Math.min(radius, w / 2, h / 2));
    const bogen = `A${z(r)} ${z(r)} 0 0 1`;
    const loch = [
        `M${z(x + r)} ${z(y)}`,
        `H${z(x + w - r)}`,
        `${bogen} ${z(x + w)} ${z(y + r)}`,
        `V${z(y + h - r)}`,
        `${bogen} ${z(x + w - r)} ${z(y + h)}`,
        `H${z(x + r)}`,
        `${bogen} ${z(x)} ${z(y + h - r)}`,
        `V${z(y + r)}`,
        `${bogen} ${z(x + r)} ${z(y)}`,
        'Z',
    ].join('');

    return `${aussen} ${loch}`;
};

// ─── Schrittwechsel ──────────────────────────────────────────────────────────

/** Nächster Index — `null` heisst: es gibt keinen mehr, die Tour ist durch. */
export const naechsterSchritt = (index: number, anzahl: number): number | null =>
    index + 1 < anzahl ? index + 1 : null;

/** Voriger Index — `null` heisst: wir stehen schon auf dem ersten Schritt. */
export const vorherigerSchritt = (index: number): number | null => (index > 0 ? index - 1 : null);

// ─── Zustand ─────────────────────────────────────────────────────────────────

export type TourPhase = 'idle' | 'laeuft' | 'abgebrochen' | 'fertig';

export interface TourStand {
    phase: TourPhase;
    kapitelId: TutorialKapitelId | null;
    /** Aktueller Schritt, 0-basiert. Bleibt beim Abbruch stehen — er wird gemeldet. */
    index: number;
    anzahl: number;
    /** Erfolglose Ankersuchen für den AKTUELLEN Schritt. */
    fehlschlaege: number;
    /** Nach `ANKER_VERSUCHE` erfolglosen Suchen: Hinweis statt Spotlight. */
    ankerFehlt: boolean;
}

/**
 * Eine Suche sofort, dann drei Wiederholungen à 400 ms (Auftrag: „3× je 400 ms").
 * Grund für die Wiederholungen: Schritt 1 klickt oft einen Knopf, der ein Modal
 * öffnet — dessen Anker existiert im Moment des Schrittwechsels noch nicht.
 */
export const ANKER_VERSUCHE = 4;
export const ANKER_WARTEN_MS = 400;

export const TOUR_START: TourStand = {
    phase: 'idle',
    kapitelId: null,
    index: 0,
    anzahl: 0,
    fehlschlaege: 0,
    ankerFehlt: false,
};

export type TourAktion =
    | { typ: 'start'; kapitelId: TutorialKapitelId; anzahl: number }
    | { typ: 'weiter' }
    | { typ: 'zurueck' }
    | { typ: 'springe'; index: number }
    | { typ: 'ankerGefunden' }
    | { typ: 'ankerFehlschlag' }
    | { typ: 'abbrechen' };

const beiSchritt = (stand: TourStand, index: number): TourStand => ({
    ...stand,
    index,
    fehlschlaege: 0,
    ankerFehlt: false,
});

/**
 * Zustandsmaschine der Tour.
 *
 * Ausserhalb von `laeuft` verpufft jede Aktion ausser `start`. Das ist
 * Absicht: `fertig` und `abgebrochen` sind Endstände, die der Container nach
 * genau einmal `onEnde` wieder abräumt. Liefe der Reducer danach weiter, käme
 * `onEnde` bei jedem Tastendruck erneut — und mit ihm `markiereAbgeschlossen`.
 */
export const tourZustand = (stand: TourStand, aktion: TourAktion): TourStand => {
    if (aktion.typ === 'start') {
        return {
            // Ein Kapitel ohne Schritte ist nichts zu zeigen — sofort durch,
            // statt eine leere Blase über dem Bildschirm stehen zu lassen.
            phase: aktion.anzahl > 0 ? 'laeuft' : 'fertig',
            kapitelId: aktion.kapitelId,
            index: 0,
            anzahl: aktion.anzahl,
            fehlschlaege: 0,
            ankerFehlt: false,
        };
    }

    if (stand.phase !== 'laeuft') return stand;

    switch (aktion.typ) {
        case 'weiter': {
            const naechster = naechsterSchritt(stand.index, stand.anzahl);
            if (naechster === null) return { ...stand, phase: 'fertig' };
            return beiSchritt(stand, naechster);
        }
        case 'zurueck': {
            const voriger = vorherigerSchritt(stand.index);
            if (voriger === null) return stand;
            return beiSchritt(stand, voriger);
        }
        case 'springe': {
            if (aktion.index < 0 || aktion.index >= stand.anzahl) return stand;
            if (aktion.index === stand.index) return stand;
            return beiSchritt(stand, aktion.index);
        }
        case 'ankerGefunden':
            if (stand.fehlschlaege === 0 && !stand.ankerFehlt) return stand;
            return { ...stand, fehlschlaege: 0, ankerFehlt: false };
        case 'ankerFehlschlag': {
            const fehlschlaege = stand.fehlschlaege + 1;
            return { ...stand, fehlschlaege, ankerFehlt: fehlschlaege >= ANKER_VERSUCHE };
        }
        case 'abbrechen':
            return { ...stand, phase: 'abgebrochen' };
    }
};

// ─── Text in der Blase ───────────────────────────────────────────────────────

const anhaengen = (vorsatz: string, stichwort: string): string => {
    const rest = stichwort.trim();
    return rest ? `${vorsatz} ${rest}` : vorsatz;
};

/**
 * Der Satz, der in der Sprechblase steht.
 *
 * `stichwort` ist der Rohstoff aus dem Register (ein Satz, was hier passiert);
 * `klick` und `tippen` bekommen davor die Handlungsaufforderung. Bei `tippen`
 * steht der Beispielwert in Anführungszeichen — und ausdrücklich als etwas, das
 * DER NUTZER eingibt, nicht die Tour (Plan §1.3).
 */
export const schrittText = (schritt: Pick<TutorialSchritt, 'aktion' | 'stichwort' | 'wert'>): string => {
    switch (schritt.aktion) {
        case 'klick':
            return anhaengen('Klicke auf das hervorgehobene Element.', schritt.stichwort);
        case 'tippen':
            return anhaengen(
                schritt.wert ? `Hier gibst du ein: „${schritt.wert}“.` : 'Hier gibst du deinen Text ein.',
                schritt.stichwort,
            );
        case 'zeigen':
        case 'warten':
            return schritt.stichwort.trim();
    }
};

/** Kurzform für Vorlesehilfen und die Fortschrittspunkte. */
export const aktionKurz = (aktion: TutorialAktion): string => {
    switch (aktion) {
        case 'klick':
            return 'Klicken';
        case 'tippen':
            return 'Eingeben';
        case 'zeigen':
            return 'Ansehen';
        case 'warten':
            return 'Warten';
    }
};

// ─── Willkommens-Tour ────────────────────────────────────────────────────────

/** `sessionStorage`: welches Kapitel nach dem Ansichtswechsel starten soll. */
export const TOUR_SCHLUESSEL = 'lernzentrum.tour';

/**
 * Wie lange eine hinterlegte Tour-Uebergabe gilt.
 *
 * Der Schluessel wird gesetzt, BEVOR navigiert wird. Erreicht die App die
 * Zielansicht nicht (Projektansicht ohne geladenes Projekt, Abbruch auf dem
 * Weg), bliebe er sonst die ganze Sitzung liegen und die Tour spraenge
 * irgendwann voellig unvermittelt an — beim naechsten Ansichtswechsel, unter
 * Umstaenden mitten in einer Eingabe. Eine Minute ist grosszuegig gerechnet
 * fuer „Ansicht wechseln, Daten laden, Anker steht im DOM" und zugleich kurz
 * genug, dass niemand den Zusammenhang zum eigenen Klick verliert.
 */
export const TOUR_UEBERGABE_GUELTIG_MS = 60_000;

/** Ergebnis von `lieseTourUebergabe` — `verfallen` heisst: Schluessel wegraeumen. */
export type TourUebergabe =
    | { art: 'keine' }
    | { art: 'verfallen' }
    | { art: 'gueltig'; id: TutorialKapitelId };

/** Was unter `TOUR_SCHLUESSEL` im `sessionStorage` steht. */
export const schreibeTourUebergabe = (id: TutorialKapitelId, jetzt: number): string =>
    JSON.stringify({ id, ts: jetzt });

/**
 * Den hinterlegten Wert auswerten — rein, damit die Verfallsregel ohne Browser
 * pruefbar ist.
 *
 * Alles, was nicht die erwartete Form hat, gilt als `verfallen` statt als
 * `keine`: dazu gehoert auch die alte Schreibweise (die blosse Kapitel-ID als
 * Text), die sonst in einem schon offenen Tab ewig haengen bliebe. `verfallen`
 * raeumt der Aufrufer weg, `keine` laesst er in Ruhe.
 */
export const lieseTourUebergabe = (
    roh: string | null,
    jetzt: number,
    gueltigMs: number = TOUR_UEBERGABE_GUELTIG_MS,
): TourUebergabe => {
    if (roh === null || roh === '') return { art: 'keine' };

    let gelesen: unknown;
    try {
        gelesen = JSON.parse(roh);
    } catch {
        return { art: 'verfallen' };
    }
    if (typeof gelesen !== 'object' || gelesen === null) return { art: 'verfallen' };

    const { id, ts } = gelesen as { id?: unknown; ts?: unknown };
    if (typeof id !== 'string' || !id || typeof ts !== 'number' || !Number.isFinite(ts)) {
        return { art: 'verfallen' };
    }
    // `jetzt < ts` faengt eine zurueckgestellte Uhr ab: ein Zeitstempel aus der
    // Zukunft wuerde sonst nie verfallen.
    const alter = jetzt - ts;
    if (alter > gueltigMs || alter < 0) return { art: 'verfallen' };

    return { art: 'gueltig', id };
};
/**
 * `localStorage`: die Willkommens-Tour lief einmal — danach nie wieder von
 * selbst.
 *
 * Seit Migration Nr. 102 ist das nur noch der ZWISCHENSPEICHER. Fuehrend ist
 * `profiles.ersteinweisung_am` (siehe `sollWillkommensTourStarten`): der
 * Merker im Browser gehoert dem Geraet, die Ersteinweisung gehoert dem
 * Nutzer.
 */
export const WILLKOMMEN_SCHLUESSEL = 'lernzentrum.willkommen';

/** Das Kapitel, das ein neuer Nutzer als erstes sieht. */
export const WILLKOMMEN_KAPITEL: TutorialKapitelId = 'einstieg.oberflaeche';

/**
 * Welches Kapitel die Willkommens-Tour zeigt — `null`, wenn keines passt.
 *
 * Freelancer bekommen KEINE: `einstieg.oberflaeche` führt durch Seitenleiste,
 * Dashboard und Projektliste, also durch Bildschirme, die ein Freelancer nie zu
 * sehen bekommt (er sieht nur `FreelancerPortal.tsx`). Eine Tour, die auf einen
 * Anker zeigt, den es für diesen Nutzer nicht gibt, ist schlimmer als keine.
 */
export const willkommensKapitel = (rollen: TutorialRolle[]): TutorialKapitelId | null =>
    rollen.some((r) => r === 'K' || r === 'I' || r === 'V') ? WILLKOMMEN_KAPITEL : null;

/**
 * Darf die Willkommens-Tour von selbst starten?
 *
 * Reine Entscheidung, damit sie ohne Browser prüfbar ist. Drei Eingaben:
 *
 *  · `gesehen` — der Merker aus `localStorage[WILLKOMMEN_SCHLUESSEL]`. Er
 *    gehört dem GERÄT und ist seit Migration Nr. 102 nur noch der
 *    Zwischenspeicher gegen einen zweiten Start im selben Sitzungslauf: das
 *    Schreiben in die Datenbank ist ein Netzweg, und bis seine Antwort da ist,
 *    steht das Profil im Zustand noch auf `null`.
 *  · `rollen` — ohne passendes Kapitel gibt es nichts zu zeigen (Freelancer).
 *  · `ersteinweisungAm` — `profiles.ersteinweisung_am`, und das ist die
 *    FÜHRENDE Quelle. Sie gehört dem NUTZER: neuer Rechner, anderer Browser,
 *    privates Fenster, geleerter Speicher — die Ersteinweisung bleibt
 *    erledigt. Und umgekehrt bekommt ein neuer Nutzer sie auch dann, wenn vor
 *    ihm schon jemand an diesem Rechner angemeldet war.
 *
 * Die Rangfolge:
 *
 *  1. Kein Kapitel für diese Rollen → nie.
 *  2. `ersteinweisungAm` trägt einen Zeitstempel → NIE. Die Datenbank
 *     überstimmt hier auch einen fehlenden Browser-Merker.
 *  3. Sonst entscheidet der Merker: `undefined` heisst „Spalte gibt es noch
 *     nicht" (Migration Nr. 102 offen) und `null` heisst „steht noch aus" —
 *     in beiden Fällen startet die Tour, solange sie in dieser Sitzung nicht
 *     schon lief.
 */
export const sollWillkommensTourStarten = (
    gesehen: boolean,
    rollen: TutorialRolle[],
    ersteinweisungAm?: string | null,
): boolean => {
    if (willkommensKapitel(rollen) === null) return false;
    // Ein leerer String ist kein Zeitstempel — er entstünde nur aus einer
    // kaputten Zeile und darf die Ersteinweisung nicht verschlucken.
    if (typeof ersteinweisungAm === 'string' && ersteinweisungAm.trim() !== '') return false;
    return !gesehen;
};
